import { IconLogout } from "@tabler/icons-react";
import { motion, useDragControls, type PanInfo } from "motion/react";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "src/authentication/hooks/useAuth";
import { PersonAvatar } from "src/people/components/PersonAvatar";
import { CopyrightFooter } from "src/privacy-policy/CopyrightFooter";
import { UpdateNotice } from "src/core/service-worker/UpdateNotice";
import { FrameHeading, SheetLink, SheetList, sheetRowClass } from "./FrameLinks";
import { sheetSpring } from "./frameMotion";
import { useFrameMenu } from "./useFrameMenu";

interface Props {
    open: boolean;
    onClose: () => void;
}

/**
 * The phone's More, as a bottom sheet. A native dialog, so focus, Escape and the top
 * layer behave as they do for every other modal; `motion` only moves it.
 */
export const MoreSheet = ({ open, onClose }: Props) => {
    const dialogRef = useRef<HTMLDialogElement>(null);
    const dragControls = useDragControls();
    const [full, setFull] = useState(false);

    const { person, logout } = useAuth();
    const { main, groups, account } = useFrameMenu();
    const notInBar = main.filter((item) => !item.bottomNav);

    /** Every ministry is a tap away on the Ministries screen, so the sheet leaves them out */
    const sheetGroups = groups.filter((group) => group.key !== "ministries");

    useEffect(() => {
        const dialog = dialogRef.current;

        if (open && dialog && !dialog.open) {
            setFull(false);
            dialog.showModal();
        }
    }, [open]);

    /** Escape asks to close, so the sheet can slide away before the dialog shuts */
    useEffect(() => {
        const dialog = dialogRef.current;
        const onCancel = (event: Event) => {
            event.preventDefault();
            onClose();
        };

        dialog?.addEventListener("cancel", onCancel);

        return () => dialog?.removeEventListener("cancel", onCancel);
    }, [onClose]);

    const onDragEnd = (_: unknown, info: PanInfo) => {
        if (info.offset.y > 100 || info.velocity.y > 600) onClose();
        else if (info.offset.y < -40) setFull(true);
    };

    return (
        <dialog
            ref={dialogRef}
            aria-label="More"
            className="lg:hidden fixed inset-0 m-0 p-0 w-full h-full max-w-none max-h-none bg-transparent backdrop:bg-transparent overflow-hidden"
        >
            <motion.div
                className="absolute inset-0 bg-black/30"
                initial={{ opacity: 0 }}
                animate={{ opacity: open ? 1 : 0 }}
                transition={{ duration: 0.2 }}
                onClick={onClose}
            />

            <motion.div
                role="document"
                className="absolute inset-x-0 bottom-0 flex flex-col rounded-t-[1.75rem] bg-frame shadow-2xl"
                initial={{ y: "100%" }}
                animate={{ y: open ? 0 : "100%", height: full ? "calc(100dvh - 2.75rem)" : "68dvh" }}
                transition={sheetSpring}
                drag="y"
                dragListener={false}
                dragControls={dragControls}
                dragConstraints={{ top: 0, bottom: 0 }}
                dragElastic={{ top: 0.08, bottom: 0.7 }}
                onDragEnd={onDragEnd}
                onAnimationComplete={() => {
                    if (!open && dialogRef.current?.open) dialogRef.current.close();
                }}
            >
                <div
                    className="shrink-0 pt-3.5 pb-2.5 cursor-grab touch-none"
                    onPointerDown={(event) => dragControls.start(event)}
                    aria-hidden="true"
                >
                    <span className="block w-8 h-1 mx-auto rounded-full bg-base-content/40" />
                </div>

                <div
                    className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 pb-[calc(2rem+env(safe-area-inset-bottom))] space-y-2"
                    // scrolling a half-open sheet opens it the rest of the way, as Material's sheets do
                    onScroll={(event) => event.currentTarget.scrollTop > 4 && setFull(true)}
                >
                    <UpdateNotice className="my-0" />

                    {notInBar.length > 0 && (
                        <SheetList>
                            {notInBar.map((item) => (
                                <SheetLink key={item.to} link={{ to: item.to, label: item.label, icon: item.icon, end: item.end }} />
                            ))}
                        </SheetList>
                    )}

                    {sheetGroups.map((group) => (
                        <section key={group.key}>
                            <FrameHeading className="pt-4">{group.label}</FrameHeading>

                            <SheetList>
                                {group.links.map((link) => (
                                    <SheetLink key={link.to} link={link} />
                                ))}
                            </SheetList>

                            {group.inactive && group.inactive.length > 0 && (
                                <>
                                    <FrameHeading className="pt-4">Inactive ministries</FrameHeading>

                                    <SheetList>
                                        {group.inactive.map((link) => (
                                            <SheetLink key={link.to} link={link} />
                                        ))}
                                    </SheetList>
                                </>
                            )}
                        </section>
                    ))}

                    {person && (
                        <section>
                            <FrameHeading className="pt-4">You</FrameHeading>

                            <SheetList>
                                <li>
                                    <div className={sheetRowClass}>
                                        <PersonAvatar person={person} size={36} />

                                        <span className="min-w-0 leading-tight">
                                            <span className="block">
                                                {person.firstName} {person.lastName}
                                            </span>

                                            {person.email && (
                                                <span className="block truncate text-sm font-normal text-base-content/70">
                                                    {person.email}
                                                </span>
                                            )}
                                        </span>
                                    </div>
                                </li>

                                {account.map((link) => (
                                    <SheetLink key={link.to} link={link} />
                                ))}

                                <li>
                                    <button type="button" onClick={logout} className={sheetRowClass}>
                                        <span className="grid place-items-center w-9 h-9 rounded-full shrink-0 bg-indicator text-on-indicator">
                                            <IconLogout className="w-5 h-5" />
                                        </span>
                                        Sign out
                                    </button>
                                </li>
                            </SheetList>
                        </section>
                    )}

                    <footer className="pt-4 text-xs">
                        <CopyrightFooter />
                    </footer>
                </div>
            </motion.div>
        </dialog>
    );
};
